import React from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Container,
  Button,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { motion } from "framer-motion";

const aiBenefits = [
  {
    title: "Automate Routine Tasks",
    points: [
      "Free up your staff by letting AI handle repetitive data entry and scheduling.",
      "Reduce human error with automated workflows.",
    ],
    image: "https://storage.googleapis.com/app_darkendimg/data-speedway/ai-agent.png",
    icon: <SmartToyIcon sx={{ fontSize: 40, color: "#facd00" }} />
  },
  {
    title: "Advanced Prompting",
    points: [
      "Custom-built prompts designed for your specific retail business needs.",
      "Extract actionable insights from your sales data instantly.",
    ],
    image: "https://storage.googleapis.com/app_darkendimg/data-speedway/ai-agent.png", // Reusing or using a relevant placeholder
    icon: <SettingsSuggestIcon sx={{ fontSize: 40, color: "#facd00" }} />
  },
  {
    title: "Intelligent Functions",
    points: [
      "Deploy AI agents that can trigger actions in your POS or inventory systems.",
      "Seamless integration with your existing tools.",
    ],
    image: "https://storage.googleapis.com/app_darkendimg/data-speedway/ai-agent.png",
    icon: <AutoFixHighIcon sx={{ fontSize: 40, color: "#facd00" }} />
  },
];

const SaaSPage = () => {
  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          position: "relative",
          height: { xs: "400px", sm: "500px", md: "600px" }, // Responsive height
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end", // Align text to the right
          paddingRight: { xs: 2, sm: 5, md: 10 }, // Adjust padding for right-aligned text
          borderRadius: 2,
          overflow: "hidden",
          mt: 1
        }}
      >
        {/* Background Image Layer (Constrained to Container) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('https://storage.googleapis.com/app_darkendimg/data-speedway/ai-agent.png')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.5)", // Darkens for readability
            zIndex: -1
          }}
        />

        {/* Animated Text Box */}
        <motion.div
          initial={{ opacity: 0, x: 50 }} // Starts off-screen to the right
          animate={{ opacity: 1, x: 0 }} // Moves into position
          transition={{ duration: 1, ease: "easeOut" }}
          style={{
            maxWidth: "600px",
            textAlign: "right",
            background: "rgba(0, 0, 0, 0.7)",
            padding: "30px",
            borderRadius: "10px",
            border: "1px solid #facd00"
          }}
        >
          <Typography variant="h3" fontWeight="bold" sx={{ color: '#fff' }}>
            Automate Your Workloads
          </Typography>
          <Typography variant="h6" sx={{ color: '#facd00', mt: 1 }}>
            Entry-Level AI for Retail & Small Business
          </Typography>
          <Typography variant="body1" color="gray" mt={2} sx={{ color: '#fff' }}>
            Unlock the power of AI Agents to streamline operations, enhance customer service, and drive efficiency without the complexity.
          </Typography>

          {/* Buttons */}
          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="contained" size="large" sx={{ backgroundColor: '#facd00', color: '#000', fontWeight: 'bold' }}>
              Get Started
            </Button>
            <Button variant="outlined" size="large" sx={{ borderColor: '#FACD00', color: '#fff' }}>
              Learn More
            </Button>
          </Box>
        </motion.div>
      </Box>

      {/* Key Benefits Section */}
      <Typography variant="h4" align="center" fontWeight="bold" mt={8} mb={5}>
        Why Choose Our AI Agents?
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {aiBenefits.map((benefit, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ boxShadow: 3, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', bgcolor: '#000' }}>
                {benefit.icon}
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
                  {benefit.title}
                </Typography>
                <List>
                  {benefit.points.map((point, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon sx={{ color: '#000' }} />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Services Breakdown */}
      <Box textAlign="center" mt={8} mb={4}>
        <Typography variant="h4" fontWeight="bold">
          Our Services
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" mt={1}>
          Tailored solutions to get you started with AI immediately.
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center" mb={5}>
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 3, bgcolor: "#f9f9f9", borderRadius: 2, height: '100%' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Advanced Prompt Engineering
            </Typography>
            <Typography variant="body1">
              We design specific prompts that turn your raw data into clear, actionable reports. No need to learn complex coding—just ask your agent.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 3, bgcolor: "#f9f9f9", borderRadius: 2, height: '100%' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Custom AI Functions
            </Typography>
            <Typography variant="body1">
              Need an agent to send an email, update a spreadsheet, or alert a manager? We build custom functions that allow your AI to take real-world actions.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Call to Action */}
      <Box textAlign="center" my={8} py={5} sx={{ bgcolor: "#000", color: "#facd00", borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          Ready to Modernize Your Business?
        </Typography>
        <Typography variant="h6" sx={{ color: '#fff', mt: 2, mb: 3 }}>
          Contact us today for a free consultation on implementing AI Agents.
        </Typography>
        <Button component="a" href="/bookit" variant="contained" size="large" sx={{ backgroundColor: '#facd00', color: '#000', fontWeight: 'bold', px: 4 }}>
          Let's Talk
        </Button>
      </Box>
    </Container>
  );
};

export default SaaSPage;
